import type { Method, ParticipantBehaviour } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormSection } from '../../../components/forms/FormSection';
import { FormStack } from '../../../components/forms/FormStack';
import { HookedDurationInput } from '../../../components/forms/HookedDurationInput';
import { HookedEpisodicSeveritySlider } from '../../../components/forms/HookedEpisodicSeveritySlider';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedFormFieldset } from '../../../components/forms/HookedFormFieldset';
import { HookedNumberInput } from '../../../components/forms/HookedNumberInput';
import { HookedSelect } from '../../../components/forms/HookedSelect';
import { HookedSingleDatepicker } from '../../../components/forms/HookedSingleDatepicker';
import { HookedTextArea } from '../../../components/forms/HookedTextArea';
import { methodOptions } from './MethodTag';
import { ParticipantBehaviourRecordingTemplateButton } from './ParticipantBehaviourRecordingTemplateButton';

interface DataMethodFormProps {
  method: Method;
}

const RecordingMethodForm = (props: DataMethodFormProps) => {
  switch (props.method.__typename) {
    case 'AbcMethod':
      return (
        <FormSection heading='ABC data'>
          <FormStack>
            <HookedFormField
              name='payload.abc.antecedent'
              label='Antecedent'
              helperText='The events, action, or circumstances that occurred before the behaviour'
              extra={
                <ParticipantBehaviourRecordingTemplateButton
                  name='payload.abc.antecedent'
                  templates={props.method.templates.antecedent}
                />
              }
            >
              <HookedTextArea
                name='payload.abc.antecedent'
                size='sm'
                placeholder='Describe the antecedent'
              />
            </HookedFormField>

            <HookedFormField
              name='payload.abc.behaviour'
              label='Behaviour'
              helperText='What happened during the behaviour?'
              extra={
                <ParticipantBehaviourRecordingTemplateButton
                  name='payload.abc.behaviour'
                  templates={props.method.templates.behaviour}
                />
              }
            >
              <HookedTextArea
                name='payload.abc.behaviour'
                size='sm'
                placeholder='Describe the behaviour'
              />
            </HookedFormField>

            <HookedFormField
              name='payload.abc.consequence'
              label='Consequence'
              helperText='The action or response that follows the behaviour.'
              extra={
                <ParticipantBehaviourRecordingTemplateButton
                  name='payload.abc.consequence'
                  templates={props.method.templates.consequence}
                />
              }
            >
              <HookedTextArea
                name='payload.abc.consequence'
                size='sm'
                placeholder='Describe the consequence'
              />
            </HookedFormField>
          </FormStack>
        </FormSection>
      );
    case 'FrequencyMethod':
      return (
        <FormSection heading='Frequency data'>
          <FormStack>
            <HookedFormField
              name='payload.frequency.value'
              label='Frequency'
              helperText='How many times did the behaviour occur?'
            >
              <HookedNumberInput name='payload.frequency.value' defaultValue='0' min={0} />
            </HookedFormField>
          </FormStack>
        </FormSection>
      );
    case 'DurationMethod':
      return (
        <FormSection heading='Duration data'>
          <FormStack>
            <HookedFormFieldset
              name='payload.duration.value'
              label='Duration'
              helperText='For how long did the behaviour occur?'
            >
              <HookedDurationInput name='payload.duration.value' />
            </HookedFormFieldset>
          </FormStack>
        </FormSection>
      );
    case 'EpisodicSeverityMethod': {
      const options = props.method.scales.map((scale) => ({
        label: scale.description,
        value: scale.id,
      }));

      return (
        <FormSection heading='Episodic severity data'>
          <FormStack>
            <HookedFormField
              name='payload.episodicSeverity.level'
              label='Episodic severity'
              helperText='What level of severity was the behaviour?'
            >
              <HookedEpisodicSeveritySlider
                name='payload.episodicSeverity.level'
                min={1}
                max={options.length}
                defaultValue={[1]}
                options={options}
              />
            </HookedFormField>
          </FormStack>
        </FormSection>
      );
    }

    default:
      return <ChakraV3.Text fontSize='sm'>Not implemented</ChakraV3.Text>;
  }
};

interface Props {
  participantBehaviours: ParticipantBehaviour[];
  isChangeBehaviourDisabled?: boolean;
  isChangeMethodDisabled?: boolean;
}

export const ParticipantBehaviourRecordingForm = (props: Props) => {
  const { participantBehaviours, isChangeBehaviourDisabled, isChangeMethodDisabled } = props;

  const { setValue, resetField, formState } = useFormContext();
  const methodType = useWatch({ name: 'method' });
  const participantBehaviourId = useWatch({ name: 'participantBehaviourId' });

  const { selectedMethod, methodOptionsPrime, participantBehaviourOptions } = React.useMemo(() => {
    const participantBehaviourOptions = participantBehaviours.map((pb) => ({
      label: pb.name,
      value: pb.id,
      raw: pb,
    }));

    const selectedParticipantBehaviour = participantBehaviours.find(
      (pb) => pb.id === participantBehaviourId,
    );
    const enabledMethods: Method[] = Object.values(
      selectedParticipantBehaviour?.methods ?? {},
    ).filter((m) => !P.isString(m) && m.enabled) as Method[];

    const selectedMethod: Method | undefined = enabledMethods.find(
      (m) => m.__typename === methodType,
    );

    const methodOptionsPrime = P.compact(enabledMethods.map((m) => methodOptions[m.__typename!]));

    return {
      selectedMethod,
      enabledMethods,
      methodOptionsPrime,
      participantBehaviourOptions,
    };
  }, [methodType, participantBehaviourId, participantBehaviours]);

  React.useEffect(() => {
    if (formState.defaultValues?.method === methodType) {
      return;
    }

    resetField('payload', {
      defaultValue: {},
    });

    switch (methodType) {
      case 'FrequencyMethod':
        setValue('payload.frequency.value', 1);
        break;
      case 'DurationMethod':
        setValue('payload.duration.value', 1);
        break;
      case 'AbcMethod':
        setValue('payload.abc.antecedent', '');
        setValue('payload.abc.behaviour', '');
        setValue('payload.abc.consequence', '');
        break;
      case 'EpisodicSeverityMethod':
        setValue('payload.episodicSeverity.level', 1);
        break;
    }
  }, [methodType, formState.defaultValues?.method, resetField, setValue]);

  return (
    <FormStack>
      <FormSection heading='Basic'>
        <FormStack>
          <HookedFormField name='participantBehaviourId' label='Behaviour'>
            <HookedSelect
              name='participantBehaviourId'
              options={participantBehaviourOptions}
              isDisabled={isChangeBehaviourDisabled}
            />
          </HookedFormField>
          <FormStack orientation='horizontal'>
            <HookedFormField
              name='method'
              label='Data collection method'
              helperText='What sort of behaviour data do you want to add?'
            >
              <HookedSelect
                name='method'
                optionVariant='tag'
                options={methodOptionsPrime}
                isDisabled={isChangeMethodDisabled}
              />
            </HookedFormField>
            <HookedFormField
              name='timestamp'
              label='Timestamp'
              helperText='When did the behaviour occur?'
            >
              <HookedSingleDatepicker name='timestamp' size='sm' />
            </HookedFormField>
          </FormStack>
        </FormStack>
      </FormSection>
      <ChakraV3.Separator />

      {selectedMethod && <RecordingMethodForm method={selectedMethod} />}
    </FormStack>
  );
};
